import { EnvironmentProviders, inject, makeEnvironmentProviders, provideAppInitializer } from '@angular/core';
import { INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG, IncludeBearerTokenCondition, provideKeycloak } from 'keycloak-angular';
import Keycloak from 'keycloak-js';
import { escapeRegExp } from 'lodash-es';
import { environment } from 'src/environments/environment';
import { KeycloakInitStatusService } from './services/keycloak-init-status.service';
import { getCurrentLanguageFromPath, getInhLanguageFromBrowser } from './utils';

// The min validity must exceed the interval, so every token is refreshed before it expires.
const TOKEN_REFRESH_INTERVAL_MS = 60_000;
const TOKEN_MIN_VALIDITY_SECONDS = 70;
// keycloak-js never times out the silent SSO iframe, e.g. when Keycloak renders an error page instead of redirecting.
const KEYCLOAK_INIT_TIMEOUT_MS = 10_000;

export function provideKeycloakAuth(): EnvironmentProviders {
    return makeEnvironmentProviders([
        provideKeycloak({ config: environment.keycloak }),
        provideAppInitializer(() => initializeKeycloak(inject(Keycloak), inject(KeycloakInitStatusService))),
        {
            provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
            useValue: createBearerTokenConditions([environment.basePath, environment.basePathCdn, environment.basePathCore])
        }
    ]);
}

export async function initializeKeycloak(keycloak: Keycloak, initStatus: KeycloakInitStatusService): Promise<void> {
    let authenticated: boolean;
    try {
        authenticated = await withTimeout(
            keycloak.init({
                onLoad: 'check-sso',
                enableLogging: environment.enableLogging,
                checkLoginIframe: false,
                silentCheckSsoRedirectUri: new URL('assets/silent-check-sso.html', document.baseURI).href
            }),
            KEYCLOAK_INIT_TIMEOUT_MS
        );
    } catch (error) {
        console.error('Keycloak initialisation failed', error);
        initStatus.markFailed();
        return;
    }
    if (!authenticated) {
        // keycloak-js's login() navigates away and never settles, so Angular is never bootstrapped.
        await keycloak.login({ locale: getLoginLocale() });
    }
    setInterval(
        () => keycloak.updateToken(TOKEN_MIN_VALIDITY_SECONDS).catch(() => keycloak.login({ locale: getLoginLocale() })),
        TOKEN_REFRESH_INTERVAL_MS
    );
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_resolve, reject) => {
        timeoutId = setTimeout(() => reject(new Error(`Timed out after ${timeoutMs} ms`)), timeoutMs);
    });
    return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId));
}

export function createBearerTokenConditions(urls: Array<string | undefined>): IncludeBearerTokenCondition[] {
    return urls
        .filter((url): url is string => !!url)
        .map((url) => ({ urlPattern: new RegExp('^' + escapeRegExp(url.replace(/\/$/, '')) + '(?=[/?#]|$)') }));
}

export function getLoginLocale(): string | undefined {
    return (getCurrentLanguageFromPath() ?? getInhLanguageFromBrowser())?.countryCode;
}
